import { z } from 'zod';
import { idSchema, isoDateSchema, isoDateTimeSchema } from './primitives';
import { lotStatusSchema } from './scanning';
/**
 * Tableau de bord et statistiques (cahier des charges §12).
 *
 * Les indicateurs sont calculés côté serveur : le client ne fait qu'afficher.
 * Chaque bloc porte sa propre période afin que les graphiques restent lisibles
 * sans recharger l'écran entier.
 */
export const dashboardRangeSchema = z.enum(['TODAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR']);
export const DASHBOARD_RANGE_LABELS = {
    TODAY: "Aujourd'hui",
    WEEK: '7 derniers jours',
    MONTH: '30 derniers jours',
    QUARTER: '3 derniers mois',
    YEAR: '12 derniers mois',
};
export const dashboardQuerySchema = z.object({
    range: dashboardRangeSchema.default('MONTH'),
    branchId: idSchema.optional(),
});
/** Indicateur cartouche avec sa variation par rapport à la période précédente. */
export const kpiSchema = z.object({
    value: z.number(),
    previousValue: z.number(),
    /** Variation relative, `null` lorsque la période précédente est vide. */
    changeRatio: z.number().nullable(),
});
export const dashboardSummarySchema = z.object({
    archivedReceipts: kpiSchema,
    lotsToday: kpiSchema,
    ocrPending: kpiSchema,
    errors: kpiSchema,
    duplicatesDetected: kpiSchema,
    pendingReview: kpiSchema,
    /** Progression du jour : reçus archivés rapportés à la moyenne quotidienne. */
    dailyProgressRatio: z.number().min(0),
    averageOcrConfidence: z.number().min(0).max(1).nullable(),
    averageProcessingMs: z.number().nonnegative().nullable(),
});
export const timeSeriesPointSchema = z.object({
    date: isoDateSchema,
    archived: z.number().int().nonnegative(),
    scanned: z.number().int().nonnegative(),
    duplicates: z.number().int().nonnegative(),
    errors: z.number().int().nonnegative(),
});
export const branchActivitySchema = z.object({
    branchId: idSchema,
    branchCode: z.string(),
    branchName: z.string(),
    archived: z.number().int().nonnegative(),
    pendingReview: z.number().int().nonnegative(),
    duplicates: z.number().int().nonnegative(),
    activeLots: z.number().int().nonnegative(),
    lastActivityAt: isoDateTimeSchema.nullable(),
});
export const storageUsageSchema = z.object({
    usedBytes: z.number().nonnegative(),
    totalBytes: z.number().nonnegative(),
    freeBytes: z.number().nonnegative(),
    documentBytes: z.number().nonnegative(),
    documentCount: z.number().int().nonnegative(),
    isLow: z.boolean(),
});
export const lotStatusBreakdownSchema = z.object({
    status: lotStatusSchema,
    count: z.number().int().nonnegative(),
});
export const dashboardSchema = z.object({
    range: dashboardRangeSchema,
    generatedAt: isoDateTimeSchema,
    summary: dashboardSummarySchema,
    series: z.array(timeSeriesPointSchema),
    monthlyTrend: z.array(z.object({ month: z.string(), archived: z.number().int().nonnegative() })),
    branches: z.array(branchActivitySchema),
    lotStatuses: z.array(lotStatusBreakdownSchema),
    storage: storageUsageSchema,
});
/** Santé du système, exposée à l'administrateur et aux sondes de supervision. */
export const healthSchema = z.object({
    status: z.enum(['UP', 'DEGRADED', 'DOWN']),
    version: z.string(),
    uptimeSeconds: z.number().nonnegative(),
    checks: z.array(z.object({
        name: z.string(),
        status: z.enum(['UP', 'DEGRADED', 'DOWN']),
        detail: z.string().nullable(),
        latencyMs: z.number().nonnegative().nullable(),
    })),
});
//# sourceMappingURL=analytics.js.map